import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { ROLE_LABELS } from "@/lib/content";

const SELECTABLE_ROLES = Object.keys(ROLE_LABELS).filter((r) => r !== "admin");

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "visitor" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell title="Create Account" subtitle="Join the Moaglee ecosystem.">
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        {error && <p className="text-red-400 text-sm" data-testid="register-error">{error}</p>}
        <div>
          <label className="overline text-neutral-400 mb-2 block">Full Name</label>
          <input className="field-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" />
        </div>
        <div>
          <label className="overline text-neutral-400 mb-2 block">Email</label>
          <input className="field-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
        </div>
        <div>
          <label className="overline text-neutral-400 mb-2 block">Password</label>
          <input className="field-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
        </div>
        <div>
          <label className="overline text-neutral-400 mb-2 block">I am a…</label>
          <select className="field-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="register-role">
            {SELECTABLE_ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </select>
        </div>
        <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="register-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Create Account"}
        </button>
      </form>
      <div className="flex items-center gap-4 my-6">
        <div className="h-px bg-white/10 flex-1" /><span className="text-neutral-600 text-xs">OR</span><div className="h-px bg-white/10 flex-1" />
      </div>
      <button onClick={googleLogin} className="btn-ghost w-full justify-center" data-testid="google-register-btn">
        Continue with Google
      </button>
      <p className="text-neutral-500 text-sm mt-8 text-center">
        Already a member? <Link to="/login" className="text-[#CC5500] hover:underline" data-testid="to-login">Log in</Link>
      </p>
    </AuthShell>
  );
}
